import { __DEV__ } from './__dev__';

export interface TreeItem {
  id: string;
  level: number;
  parent: string | null;
  children?: TreeItem[];
}

export interface BuildTreeResult {
  root: TreeItem | null;
  descendants: Record<string, Set<string>> | null;
  list: TreeItem[] | null;
}

export interface MapItem {
  id: string;
  parent: string | null;
  name: string;
  sortIndex?: number | null;
}

export type MapType = Record<string, MapItem>;

export function buildTree(map: MapType | null): BuildTreeResult {
  if (map == null) return { root: null, descendants: null, list: null };

  const { mapping, list } = Object.values(map).reduce<{
    mapping: Record<string, number>;
    list: TreeItem[];
  }>(
    (accumulator, item, index) => {
      accumulator.mapping[item.id] = index;
      accumulator.list.push({
        id: item.id,
        level: getLevel(item, map),
        parent: item.parent,
      });
      return accumulator;
    },
    {
      mapping: {},
      list: [],
    },
  );

  const ghosts: TreeItem[] = [];

  const root = list.reduce<TreeItem | null>((accumulator, item) => {
    if (item.parent === null) {
      accumulator = item;
      return accumulator;
    }
    const parent = list[mapping[item.parent]];
    if (parent) {
      parent.children = [...(parent.children || []), item];
    } else {
      ghosts.push(item);
    }
    return accumulator;
  }, null);

  if (root) {
    root.children = root.children?.concat(ghosts) ?? [...ghosts];
  }

  const descendants: Record<string, Set<string>> = list.reduce(
    (accumulator, item) => {
      accumulator[item.id] = item.children ? new Set(flatten(item.children)) : new Set();
      return accumulator;
    },
    {} as Record<string, Set<string>>,
  );

  const sortedList = root ? getSortedList(root, [], map) : [];

  return {
    root,
    descendants,
    list: sortedList,
  };
}

function getSortedList(root: TreeItem, array: TreeItem[], map: MapType): TreeItem[] {
  if (root == null) return array;
  array.push(root);
  if (root.children) {
    const entries = Object.entries(root.children).sort(([, firstChild], [, secondChild]) => {
      const firstChildIndex = map[firstChild.id].sortIndex ?? Infinity;
      const secondChildIndex = map[secondChild.id].sortIndex ?? Infinity;

      if (firstChildIndex !== secondChildIndex) {
        return firstChildIndex - secondChildIndex;
      }

      return map[firstChild.id].name.localeCompare(map[secondChild.id].name, undefined, {
        sensitivity: 'base',
      });
    });
    entries.forEach(([, child]) => {
      getSortedList(child, array, map);
    });
  }
  return array;
}

function flatten(children: TreeItem[]): string[] {
  let result: string[] = [];
  children.forEach((child) => {
    result.push(child.id);
    if (Array.isArray(child.children)) {
      result = result.concat(flatten(child.children));
    }
  });
  return result;
}

function getLevel(
  item: MapItem,
  map: MapType,
  initialCount = 0,
  visited = new Set<string>(),
): number {
  if (visited.has(item.id)) {
    __DEV__ && console.warn('Circular reference detected in buildTree', item);
    return initialCount;
  }
  visited.add(item.id);
  let count = initialCount;
  if (item.parent === null) {
    return count;
  }
  count = count + 1;
  const parent = map[item.parent];
  if (parent) {
    return getLevel(map[item.parent], map, count, visited);
  } else {
    __DEV__ && console.log(map[item.id].name);
  }
  return count;
}
